import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { differenceInMinutes, format, fromUnixTime } from 'date-fns';
import * as _ from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { DateChunk, SenderChunk, TimeChunk, TranscriptMessage } from '../transcript-message';

@Component({
    selector: 'app-transcript',
    templateUrl: './transcript.component.html',
    styleUrls: ['./transcript.component.scss'],
    standalone: false
})
export class TranscriptComponent implements OnInit, OnChanges {
    @Input() messages: TranscriptMessage[] = [];
    dateChunks: DateChunk[];
    now$: Observable<number>;

    constructor() {
    }

    ngOnInit() {
        const nowBS = new BehaviorSubject(Date.now());
        setInterval(() => {
            nowBS.next(Date.now());
        }, 60 * 1000);
        this.now$ = nowBS.asObservable();
    }

    ngOnChanges() {
        this.dateChunks = this.groupMessagesByDate(this.messages);
    }

    groupMessagesByTime(messages: TranscriptMessage[]): TimeChunk[] {
        return _(messages)
            .orderBy('modifiedTimestamp', 'desc')
            .reduce((acc: Array<Array<TranscriptMessage>>, cur: TranscriptMessage) => {
                if (!acc.length) {
                    return acc.concat([[cur]]);
                }
                const lastMessage = acc[acc.length - 1][0];
                const diff = differenceInMinutes(fromUnixTime(lastMessage.modifiedTimestamp), fromUnixTime(cur.modifiedTimestamp));
                if (diff < 10) {
                    acc[acc.length - 1].push(cur);
                    return acc;
                }
                return acc.concat([[cur]]);
            }, [])
            .map(messageChunk => ({
                startTimestamp: messageChunk[0].modifiedTimestamp,
                messages: messageChunk
            }));
    }

    groupMessagesBySender(messages: TranscriptMessage[]): SenderChunk[] {
        return _(messages)
            .orderBy('modifiedTimestamp', 'desc')
            .reduce((acc: Array<Array<TranscriptMessage>>, cur: TranscriptMessage) => {
                if (!acc.length) {
                    return acc.concat([[cur]]);
                }
                const lastMessage = acc[acc.length - 1][0];
                if (lastMessage.fromUser.id === cur.fromUser.id) {
                    acc[acc.length - 1].push(cur);
                    return acc;
                }
                return acc.concat([[cur]]);
            }, [])
            .map(messageChunk => ({
                sender: messageChunk[0].fromUser,
                timeChunks: this.groupMessagesByTime(messageChunk),
                templateId: messageChunk[0].templateId,
                isAutomated: messageChunk[0].isAutomated,
                automatedAttemptNumber: messageChunk[0].automatedAttemptNumber
            }));
    }

    groupMessagesByDate(messages: TranscriptMessage[]): DateChunk[] {
        return _(messages)
            .groupBy(message => format(fromUnixTime(message.modifiedTimestamp), 'yyyy-MM-dd'))
            .map((messageChunk, date) => ({
                date,
                senderChunks: this.groupMessagesBySender(messageChunk)
            }))
            .orderBy('date', 'desc')
            .value();
    }

    setMessageConditionalStyles(message: TranscriptMessage, lastMessage: boolean, lastTimeGroup: boolean): string {
      let style = '';
      if (message.errorMessage) {
        style += 'transcript-error-alert ';
      }
      if (message.type) {
        if (message.type === 'incoming') {
          style += 'incoming ';
        } else {
          style += 'outgoing ';
        }
      }
      if (lastMessage && lastTimeGroup) {
        style += 'last-message mt-1 mb-4';
      } else {
        style += 'my-1';
      }
      return style;
    }

    setTimestampText(message: TranscriptMessage): string {
      if (message.errorMessage) {
        return 'Failed';
      } else {
        if (message.type === 'incoming') {
          return 'Received';
        } else {
          return 'Delivered';
        }
      }
    }
}
